/**
 * Pure calculation functions for departure scenarios
 * Extracted from LineChart.js, mail.html.js, and graphs.js
 *
 * These functions contain no side effects and can be easily tested.
 */

// Ship preparation time (must match DepartureOptions.js SHIP_PREPARATION_HOURS)
const SHIP_PREPARATION_MS = 1 * 60 * 60 * 1000 // 1 hour in ms
const ONE_MINUTE_MS = 60 * 1000

/**
 * Calculate the minimum tide level required for vessel departure
 * This is the threshold line shown on the graph
 *
 * Formula: boat_draft + tolerance - depth
 *
 * @param {number} boatDraft - Vessel draft in meters
 * @param {number} tolerance - Under-keel safety clearance in meters (fixed at 0.75m)
 * @param {number} depth - Dock depth (or shoal depth) in meters
 * @returns {number} Minimum tide level in meters
 */
export function calculateMinTide(boatDraft, tolerance, depth) {
    const result = parseFloat(boatDraft) + parseFloat(tolerance) - parseFloat(depth)
    return parseFloat(result.toFixed(2))
}

/**
 * Check if an analysis has a shoal defined (determines pilot sections)
 *
 * @param {number|null} dockShoal - The shoal depth value
 * @returns {boolean} True if shoal exists and is valid
 */
export function hasShoal(dockShoal) {
    return dockShoal !== undefined && dockShoal !== null && dockShoal !== 0
}

/**
 * Calculate the boat draft line values for each tide data point
 * This represents how much clearance the boat has at each moment
 *
 * From LineChart.js:format() - the boatDraft line shows: tide - boatDraft
 *
 * @param {Array} tideData - Array of tide data points with wlo, wlp, wlf-spine
 * @param {number} boatDraft - Vessel draft in meters
 * @returns {Array} Array with date and boatDraft (clearance) values
 */
export function calculateBoatDraftLine(tideData, boatDraft) {
    return tideData
        .map(d => {
            // Use observed (wlo) if available, otherwise use the lower of wlp and wlf-spine
            const tide =
                d.wlo !== null && d.wlo !== 0
                    ? d.wlo
                    : d.wlp >= d['wlf-spine']
                    ? d['wlf-spine']
                    : d.wlp

            if (!tide) return null

            return {
                date: d.date,
                boatDraft: tide - parseFloat(boatDraft),
            }
        })
        .filter(d => d !== null)
}

/**
 * Round a date UP to the next minute (ceiling)
 * Used for START times to ensure tide is definitely above threshold
 *
 * @param {Date} date - The date to round
 * @returns {Date} The date rounded up to next minute
 */
export function roundUpToMinute(date) {
    if (!date) return null
    const ms = new Date(date).getTime()
    return new Date(Math.ceil(ms / 60000) * 60000)
}

/**
 * Round a date DOWN to the previous minute (floor)
 * Used for END times to ensure ship leaves before tide drops
 *
 * @param {Date} date - The date to round
 * @returns {Date} The date rounded down to previous minute
 */
export function roundDownToMinute(date) {
    if (!date) return null
    const ms = new Date(date).getTime()
    return new Date(Math.floor(ms / 60000) * 60000)
}

/**
 * Interpolate the exact crossing time between two data points
 * Uses linear interpolation to find when the value crosses the threshold
 *
 * @param {Object} pointBefore - Data point before crossing { date, boatDraft }
 * @param {Object} pointAfter - Data point after crossing { date, boatDraft }
 * @param {number} threshold - The threshold value being crossed
 * @returns {Date} The interpolated crossing time
 */
export function interpolateCrossingTime(pointBefore, pointAfter, threshold) {
    if (!pointBefore || !pointAfter) return null

    const time1 = new Date(pointBefore.date).getTime()
    const time2 = new Date(pointAfter.date).getTime()
    const value1 = pointBefore.boatDraft
    const value2 = pointAfter.boatDraft

    // Avoid division by zero
    if (value2 === value1) return new Date(time1)

    // Linear interpolation: find t where value crosses threshold
    const ratio = (threshold - value1) / (value2 - value1)

    // Clamp ratio to [0, 1] to ensure result is between the two points
    // This handles edge cases where threshold is outside the value range
    const clampedRatio = Math.max(0, Math.min(1, ratio))
    const crossingTime = time1 + clampedRatio * (time2 - time1)

    return new Date(crossingTime)
}

/**
 * Find departure windows where the tide is above the minimum threshold
 * This is the core logic from LineChart.js:get_seuil_areas()
 *
 * A departure window is a continuous period where:
 * boatDraft (clearance) >= threshold (min_tide relative to zero)
 *
 * Note: In the graph, the threshold is represented as: -dockDepth + tolerance
 * And boatDraft values are: tide - boatDraft
 * So we're checking: (tide - boatDraft) >= (-dockDepth + tolerance)
 * Which simplifies to: tide >= boatDraft + tolerance - dockDepth = minTide
 *
 * @param {Array} boatDraftData - Array from calculateBoatDraftLine()
 * @param {number} threshold - The threshold value (-dockDepth + tolerance)
 * @param {Date|string} now - Current time (windows must be in the future)
 * @returns {Array} Array of departure windows, each containing array of data points.
 *                  Each area includes interpolatedStart and interpolatedEnd for accurate
 *                  visual display of crossing times.
 */
export function findDepartureWindows(boatDraftData, threshold, now = new Date()) {
    const nowTime = new Date(now).getTime()
    const areas = []
    let currentArea = null
    let areaStartIndex = null

    boatDraftData.forEach((d, index) => {
        const dataTime = new Date(d.date).getTime()

        // Only consider future times
        if (dataTime > nowTime) {
            if (d.boatDraft >= threshold) {
                // We're in a valid departure window
                if (currentArea === null) {
                    currentArea = []
                    areaStartIndex = index
                }
                currentArea.push(d)
            } else {
                // We've exited a departure window
                if (currentArea !== null && currentArea.length > 0) {
                    // Calculate interpolated crossing times
                    const prevPoint = areaStartIndex > 0 ? boatDraftData[areaStartIndex - 1] : null
                    let interpolatedStart = interpolateCrossingTime(
                        prevPoint,
                        currentArea[0],
                        threshold
                    )
                    let interpolatedEnd = interpolateCrossingTime(
                        currentArea[currentArea.length - 1],
                        d,
                        threshold
                    )

                    // Apply safe rounding: start UP, end DOWN
                    // This ensures we're always within the safe departure window
                    interpolatedStart = roundUpToMinute(interpolatedStart)
                    interpolatedEnd = roundDownToMinute(interpolatedEnd)

                    // Validate: interpolated times must be in correct order
                    // If not, fall back to data point times
                    if (interpolatedStart && interpolatedEnd) {
                        const startTime = new Date(interpolatedStart).getTime()
                        const endTime = new Date(interpolatedEnd).getTime()
                        if (startTime >= endTime) {
                            // Interpolation produced invalid result, use data point times
                            interpolatedStart = null
                            interpolatedEnd = null
                        }
                    }

                    currentArea.interpolatedStart = interpolatedStart
                    currentArea.interpolatedEnd = interpolatedEnd
                    areas.push(currentArea)
                    currentArea = null
                    areaStartIndex = null
                }
            }
        }
    })

    // Don't forget the last area if we ended inside one
    if (currentArea !== null && currentArea.length > 0) {
        // Calculate interpolated start (no end point available - window extends beyond data)
        const prevPoint = areaStartIndex > 0 ? boatDraftData[areaStartIndex - 1] : null
        const rawStart = interpolateCrossingTime(prevPoint, currentArea[0], threshold)

        // Apply safe rounding: start UP (end is null - window extends beyond data)
        currentArea.interpolatedStart = roundUpToMinute(rawStart)
        currentArea.interpolatedEnd = null // Window extends beyond available data
        areas.push(currentArea)
    }

    return areas
}

/**
 * Extract departure window boundaries (start and end times)
 * Used when saving the selected departure window
 *
 * @param {Array} windowData - Single departure window from findDepartureWindows()
 * @returns {Object} { start: Date, end: Date }
 */
export function getWindowBoundaries(windowData) {
    if (!windowData || windowData.length === 0) {
        return { start: null, end: null }
    }

    return {
        start: windowData[0].date,
        end: windowData[windowData.length - 1].date,
    }
}

/**
 * Calculate mobilization time (1 hour before departure)
 * From mail.html.js - the maximum time for crew mobilization
 *
 * @param {Date|string} departureTime - The departure time
 * @returns {Date} Mobilization deadline (1 hour before)
 */
export function calculateMobilizationTime(departureTime) {
    const time = new Date(departureTime)
    return new Date(time.getTime() - 60 * 60 * 1000) // Subtract 1 hour
}

/**
 * Round time down to nearest 15-minute interval
 * Used in mail.html.js for displaying mobilization times
 *
 * @param {Date|string} time - The time to round
 * @returns {Date} Rounded time
 */
export function roundToQuarterHour(time) {
    const date = new Date(time)
    const minutes = date.getMinutes()
    const roundedMinutes = Math.floor(minutes / 15) * 15
    date.setMinutes(roundedMinutes)
    date.setSeconds(0)
    date.setMilliseconds(0)
    return date
}

/**
 * Check if a departure window is too short (less than 1 hour)
 * If so, a warning message should be shown instead of times
 *
 * @param {Date|string} start - Window start time
 * @param {Date|string} end - Window end time
 * @returns {boolean} True if window is less than 1 hour
 */
export function isWindowTooShort(start, end) {
    if (!start || !end) return true
    const startTime = new Date(start).getTime()
    const endTime = new Date(end).getTime()
    const durationMs = endTime - startTime
    return durationMs < 60 * 60 * 1000 // Less than 1 hour
}

/**
 * Get the effective tide level at a specific time
 * Uses observed (wlo) if available, otherwise uses the lower of wlp and wlf-spine
 *
 * From graphs.js:findTideLevelAtHour()
 *
 * @param {Array} tideData - Array of tide data points
 * @param {Date|string} targetTime - The time to find
 * @returns {number|null} Tide level in meters
 */
export function getTideLevelAtTime(tideData, targetTime) {
    // Handle null/undefined inputs
    if (!tideData || !targetTime) return null

    const targetMs = new Date(targetTime).getTime()

    // Find the closest data point
    let closest = null
    let closestDiff = Infinity

    for (const t of tideData) {
        const diff = Math.abs(new Date(t.date).getTime() - targetMs)
        if (diff < closestDiff) {
            closestDiff = diff
            closest = t
        }
    }

    if (!closest) return null

    // Use observed if available, otherwise use lower of wlp and wlf-spine
    if (closest.wlo && closest.wlo !== 0) {
        return closest.wlo
    }

    const wlfSpine = closest['wlf-spine']
    if (wlfSpine && closest.wlp) {
        return wlfSpine <= closest.wlp ? wlfSpine : closest.wlp
    }

    return closest.wlp || wlfSpine || null
}

/**
 * Calculate all departure data for an analysis
 * This is the main entry point that combines all calculations
 *
 * @param {Object} params
 * @param {number} params.boatDraft - Vessel draft in meters
 * @param {number} params.tolerance - Under-keel clearance in meters
 * @param {number} params.dockDepth - Dock depth in meters
 * @param {number|null} params.dockShoal - Shoal depth in meters (optional)
 * @param {string} params.calculationMethod - 'latest_time' or 'departure_range'
 * @param {Array} params.tideData - Array of tide data points
 * @param {Date|string} params.now - Current time
 * @returns {Object} Calculated departure data
 */
export function calculateDepartureData({
    boatDraft,
    tolerance,
    dockDepth,
    dockShoal,
    calculationMethod,
    tideData,
    now = new Date(),
}) {
    const hasShoalValue = hasShoal(dockShoal)

    // Calculate minimum tides
    const minTide = calculateMinTide(boatDraft, tolerance, dockDepth)
    const minTideShoal = hasShoalValue ? calculateMinTide(boatDraft, tolerance, dockShoal) : null

    // Calculate boat draft line
    const boatDraftLine = calculateBoatDraftLine(tideData, boatDraft)

    // Calculate thresholds (as used in the graph: -depth + tolerance)
    const threshold = -parseFloat(dockDepth) + parseFloat(tolerance)
    const thresholdShoal = hasShoalValue ? -parseFloat(dockShoal) + parseFloat(tolerance) : null

    // Find departure windows
    const windows = findDepartureWindows(boatDraftLine, threshold, now)
    const windowsShoal = hasShoalValue
        ? findDepartureWindows(boatDraftLine, thresholdShoal, now)
        : []

    return {
        // Calculation method
        calculationMethod,

        // Minimum tide levels
        minTide,
        minTideShoal,

        // Whether this analysis has pilot sections
        hasShoal: hasShoalValue,

        // Thresholds (for graph display)
        threshold,
        thresholdShoal,

        // Departure windows
        windows,
        windowsShoal,

        // Boat draft line data (for graph)
        boatDraftLine,
    }
}

/**
 * Format a number to 2 decimal places (matching mail.html.js formatNum)
 *
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
export function formatNum(num) {
    if (!num && num !== 0) return (0).toFixed(2)
    return parseFloat(num).toFixed(2)
}

/**
 * Find peaks (high and low tides) in tide data
 * Extracted from LineChart.js:getPeaks()
 *
 * @param {Array} data - Array of tide data points with date and value properties
 * @param {string} key - The key to use for values (e.g., 'wlo', 'wlf-spine')
 * @returns {Array} Array of peak objects with date, value, and className
 */
export function getPeaks(data, key) {
    try {
        if (!data || data.length < 2) return []

        let dir = data[0]?.[key] > data[1]?.[key] ? 'down' : 'up'
        let pastPeak
        const toRemove = []

        return data
            .filter((d, i) => {
                const first = i <= 6

                const validation = () => {
                    if (first) return false
                    if (pastPeak !== undefined && isSameHour(d.date, data[pastPeak].date)) {
                        if (d?.[key] < data[pastPeak]?.[key]) return false
                        else toRemove[toRemove.length - 1] = true
                    }

                    pastPeak = i
                    toRemove.push(false)
                    return true
                }

                const nextDiff = data[i + 1]?.[key] - data[i]?.[key]
                if (dir === 'down' && nextDiff > 0) {
                    dir = 'up'
                    return validation()
                }

                if (dir === 'up' && nextDiff < 0) {
                    dir = 'down'
                    return validation()
                }

                return false
            })
            .filter((d, i) => !toRemove[i])
            .map(d => ({ ...d, value: d[key], className: key }))
    } catch (e) {
        console.log(e)
        return []
    }
}

/**
 * Check if two dates are in the same hour
 * Helper for getPeaks()
 *
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {boolean} True if same hour
 */
function isSameHour(date1, date2) {
    const d1 = new Date(date1)
    const d2 = new Date(date2)
    return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate() &&
        d1.getHours() === d2.getHours()
    )
}

/**
 * Calculate mobilization time with rounding to 15-minute intervals
 * This is the display format used in emails
 *
 * Formula: departureTime - 1 hour, rounded down to nearest 15 minutes
 *
 * @param {Date|string} departureTime - The departure time
 * @returns {Date} Mobilization time rounded to 15 minutes
 */
export function calculateMobilizationTimeRounded(departureTime) {
    const time = calculateMobilizationTime(departureTime)
    return roundToQuarterHour(time)
}

/**
 * Transform shoal windows array for use in area click handler
 * Extracted from LineChart.js:modifyArray()
 *
 * @param {Array} windows - Array of departure windows
 * @returns {Array} Array of {min, max} objects with date boundaries
 */
export function transformWindowsForShoal(windows) {
    if (!windows || !Array.isArray(windows)) return []
    return windows.map(subArray => {
        if (!subArray || subArray.length === 0) return { min: null, max: null }
        const firstElement = subArray[0]
        const lastElement = subArray[subArray.length - 1]
        return { min: firstElement.date, max: lastElement.date }
    })
}

/**
 * Find the peak (highest boatDraft value) within a departure window
 * This is the high tide point where the tide transitions from rising to descending
 *
 * @param {Array} windowData - Single departure window array with date and boatDraft
 * @returns {Object|null} Peak point {date, boatDraft, index} or null if no peak found
 */
export function findPeakInWindow(windowData) {
    if (!windowData || windowData.length === 0) return null

    let peakIndex = 0
    let peakValue = windowData[0].boatDraft

    for (let i = 1; i < windowData.length; i++) {
        if (windowData[i].boatDraft > peakValue) {
            peakValue = windowData[i].boatDraft
            peakIndex = i
        }
    }

    return {
        date: windowData[peakIndex].date,
        boatDraft: peakValue,
        index: peakIndex,
    }
}

/**
 * Split a departure window into rising and descending portions at the peak
 * Used for Scenario 4 (departure_range_pilot_updown) which shows two time ranges
 *
 * @param {Array} windowData - Single departure window array with date and boatDraft
 * @returns {Object} { rising: {start, end} | null, descending: {start, end} | null }
 */
export function splitWindowAtPeak(windowData) {
    if (!windowData || windowData.length === 0) {
        return { rising: null, descending: null }
    }

    const peak = findPeakInWindow(windowData)
    if (!peak) {
        return { rising: null, descending: null }
    }

    const risingPortion = windowData.slice(0, peak.index + 1)
    const descendingPortion = windowData.slice(peak.index)

    return {
        rising:
            risingPortion.length > 1
                ? {
                      start: risingPortion[0].date,
                      end: risingPortion[risingPortion.length - 1].date,
                  }
                : null,
        descending:
            descendingPortion.length > 1
                ? {
                      start: descendingPortion[0].date,
                      end: descendingPortion[descendingPortion.length - 1].date,
                  }
                : null,
    }
}

/**
 * Calculate dual-range departure options for Scenario 4 (pilot up/down)
 * Each option contains a rising range and a descending range
 *
 * Per spec (SCENARIOS-2025.md):
 * - Range #1 (rising): rising tide portion before peak
 * - Range #2 (descending): descending tide portion after peak
 * - Each range independently checked for < 1h
 *
 * @param {Array} windows - Array of departure windows from findDepartureWindows()
 * @param {number} maxOptions - Maximum number of options to return (default 4)
 * @returns {Array} Array of options, each with { rising: {start, end, windowTooShort}, descending: {start, end, windowTooShort} }
 */
export function calculateDualRangeOptions(windows, maxOptions = 4) {
    if (!windows || windows.length === 0) {
        return []
    }

    return windows.slice(0, maxOptions).map(windowData => {
        const { rising, descending } = splitWindowAtPeak(windowData)

        return {
            rising: rising
                ? {
                      start: rising.start,
                      end: rising.end,
                      windowTooShort: isWindowTooShort(rising.start, rising.end),
                  }
                : null,
            descending: descending
                ? {
                      start: descending.start,
                      end: descending.end,
                      windowTooShort: isWindowTooShort(descending.start, descending.end),
                  }
                : null,
        }
    })
}

/**
 * Calculate EXTRA time windows for pilot scenarios
 *
 * Per SCENARIOS-2025.md spec:
 * - Sans pilote (shoal-based): B-C window (narrower)
 * - Avec pilote (dock-based): A-D window (wider)
 * - EXTRA times = the difference between dock and shoal windows
 *   - Rising (A-B): From dock window start to shoal window start
 *   - Descending (C-D): From shoal window end to dock window end
 *
 * Tide Level
 *     ^
 *     |           /\
 *     |          /  \
 * 2.1m --------/----\------- Sans pilote threshold (B-C)
 *     |       /      \
 * 1.1m -----/----------\----- Avec pilote threshold (A-D)
 *     |    /            \
 *     +----------------------> Time
 *         |  |        |  |
 *         A  B        C  D
 *
 * B-C: Sans pilote window
 * A-B + C-D: EXTRA times only available with LPA pilot
 *
 * @param {Object} pilotWindow - Dock-based window {start, end} (wider, A-D)
 * @param {Object} regularWindow - Shoal-based window {start, end} (narrower, B-C)
 * @returns {Object} { rising: {start, end, windowTooShort} | null, descending: {start, end, windowTooShort} | null }
 */
export function calculateExtraWindows(pilotWindow, regularWindow) {
    // Handle null/undefined inputs
    if (!pilotWindow || (!pilotWindow.start && !pilotWindow.end)) {
        return { rising: null, descending: null }
    }

    // If no regular window, the entire pilot window is "extra" (shouldn't happen in practice)
    if (!regularWindow || (!regularWindow.start && !regularWindow.end)) {
        return {
            rising: null,
            descending:
                pilotWindow.start && pilotWindow.end
                    ? {
                          start: pilotWindow.start,
                          end: pilotWindow.end,
                          windowTooShort: isWindowTooShort(pilotWindow.start, pilotWindow.end),
                      }
                    : null,
        }
    }

    const pilotStart = pilotWindow.start ? new Date(pilotWindow.start).getTime() : null
    const pilotEnd = pilotWindow.end ? new Date(pilotWindow.end).getTime() : null
    const regularStart = regularWindow.start ? new Date(regularWindow.start).getTime() : null
    const regularEnd = regularWindow.end ? new Date(regularWindow.end).getTime() : null

    // Calculate rising EXTRA (A-B): from pilot start to regular start
    let rising = null
    if (pilotStart && regularStart && pilotStart < regularStart) {
        rising = {
            start: pilotWindow.start,
            end: regularWindow.start,
            windowTooShort: isWindowTooShort(pilotWindow.start, regularWindow.start),
        }
    }

    // Calculate descending EXTRA (C-D): from regular end to pilot end
    // The start is adjusted by -SHIP_PREPARATION + 1min so the pilot window
    // begins right after the displayed "sans pilote" end (which subtracts 1h prep)
    let descending = null
    if (regularEnd && pilotEnd && regularEnd < pilotEnd) {
        const adjustedStart = new Date(
            regularEnd - SHIP_PREPARATION_MS + ONE_MINUTE_MS
        ).toISOString()
        descending = {
            start: adjustedStart,
            end: pilotWindow.end,
            windowTooShort: isWindowTooShort(adjustedStart, pilotWindow.end),
        }
    }

    return { rising, descending }
}

/**
 * Check if two time windows overlap (are from the same tidal peak)
 */
function windowsOverlap(window1, window2) {
    if (!window1?.start || !window1?.end || !window2?.start || !window2?.end) {
        return false
    }
    const start1 = new Date(window1.start).getTime()
    const end1 = new Date(window1.end).getTime()
    const start2 = new Date(window2.start).getTime()
    const end2 = new Date(window2.end).getTime()

    // Windows overlap if they share any time range
    return start1 < end2 && end1 > start2
}

/**
 * Calculate EXTRA time options for all 4 departure windows
 *
 * Handles array misalignment: if pilot and regular options don't overlap
 * in time (different peaks), treats it as pilot-only for that window.
 *
 * @param {Array} pilotOptions - Array of dock-based departure options (wider windows)
 * @param {Array} regularOptions - Array of shoal-based departure options (narrower windows)
 * @param {number} maxOptions - Maximum number of options (default 4)
 * @returns {Array} Array of EXTRA time options, each with { rising, descending }
 */
export function calculateExtraTimeOptions(pilotOptions, regularOptions, maxOptions = 4) {
    const results = []

    for (let i = 0; i < maxOptions; i++) {
        const pilotOpt = pilotOptions?.[i] || { start: null, end: null }
        const regularOpt = regularOptions?.[i] || { start: null, end: null }

        // Check if windows actually overlap (same peak)
        // If not, arrays are misaligned - treat as pilot-only
        const aligned = windowsOverlap(pilotOpt, regularOpt)
        const effectiveRegularOpt = aligned ? regularOpt : { start: null, end: null }

        results.push(calculateExtraWindows(pilotOpt, effectiveRegularOpt))
    }

    return results
}

/**
 * Map dock departure windows to their corresponding shoal departure windows
 *
 * This function handles the case where not all dock windows have corresponding
 * shoal windows. For pilot scenarios:
 * - Dock windows (avec pilote) can be wider because pilots can navigate the shoal
 * - Shoal windows (sans pilote) are narrower and require more tide
 * - Some tides may only cross the dock threshold (pilot-only peaks)
 *
 * Example scenario:
 * - Peak 1: dock window exists, no shoal window (pilot-only)
 * - Peak 2: dock window exists, shoal window exists (both options available)
 * - Peak 3: dock window exists, no shoal window (pilot-only)
 *
 * @param {Array} dockWindows - Array of dock departure windows [{start, end, windowTooShort}]
 * @param {Array} shoalAreas - Array of shoal area data points [[{date, boatDraft}, ...], ...]
 * @returns {Array} Array of shoal windows mapped to each dock window position
 *                  Returns {start: null, end: null, windowTooShort: false} for pilot-only windows
 */
export function mapDockWindowsToShoalWindows(dockWindows, shoalAreas) {
    if (!dockWindows || dockWindows.length === 0) {
        return []
    }

    if (!shoalAreas || shoalAreas.length === 0) {
        // No shoal areas at all - all windows are pilot-only
        return dockWindows.map(() => ({ start: null, end: null, windowTooShort: false }))
    }

    return dockWindows.map(dockWindow => {
        if (!dockWindow.start || !dockWindow.end) {
            return { start: null, end: null, windowTooShort: false }
        }

        const dockStart = new Date(dockWindow.start).getTime()
        const dockEnd = new Date(dockWindow.end).getTime()

        // Find the shoal area that overlaps with this dock window
        const matchingShoal = shoalAreas.find(shoalArea => {
            if (!shoalArea || shoalArea.length === 0) return false

            const shoalStart = new Date(shoalArea[0].date).getTime()
            const shoalEnd = new Date(shoalArea[shoalArea.length - 1].date).getTime()

            // Shoal area overlaps with dock area if they share any time range
            return shoalStart < dockEnd && shoalEnd > dockStart
        })

        if (!matchingShoal || matchingShoal.length === 0) {
            // No shoal window for this dock window (pilot-only)
            return { start: null, end: null, windowTooShort: false }
        }

        // Use interpolated crossing times for accurate display
        // Fall back to data point times if interpolation unavailable
        const shoalStart = matchingShoal.interpolatedStart || matchingShoal[0].date
        const shoalEnd =
            matchingShoal.interpolatedEnd || matchingShoal[matchingShoal.length - 1].date
        const duration = new Date(shoalEnd) - new Date(shoalStart)

        return {
            start: shoalStart,
            end: shoalEnd,
            windowTooShort: duration < 60 * 60 * 1000,
        }
    })
}

/**
 * Map dock departure windows to shoal windows using min/max format
 * This variant accepts shoal windows in {min, max} format (used by Timeline.js)
 *
 * @param {Array} dockWindows - Array of dock departure windows [{start, end, windowTooShort}]
 * @param {Array} shoalWindows - Array of shoal windows in {min, max} format
 * @returns {Array} Array of shoal windows mapped to each dock window position
 */
/**
 * Determine which windows to save as departure_windows based on scenario type
 *
 * For pilot scenarios (with shoal): use shoal windows (sans pilote) if available,
 * otherwise return empty array (all peaks are pilot-only)
 * For non-pilot scenarios (no shoal): use dock windows
 *
 * @param {Array} dockWindows - Dock-based departure windows [{start, end, windowTooShort}]
 * @param {Array} shoalWindows - Shoal-based departure windows (may have null start/end)
 * @param {boolean} isPilotScenario - Whether this is a pilot scenario (shoal configured)
 * @returns {Array} The appropriate windows to save as departure_windows
 */
export function selectDepartureWindows(dockWindows, shoalWindows, isPilotScenario = false) {
    // Check if any shoal window has valid (non-null) start/end values
    const hasValidShoalWindows =
        shoalWindows && shoalWindows.some(w => w && w.start !== null && w.end !== null)

    if (hasValidShoalWindows) {
        // Pilot scenario with valid shoal windows - use shoal windows
        return shoalWindows
    }

    if (isPilotScenario) {
        // Pilot scenario but no valid shoal windows (all peaks are pilot-only)
        // Return empty array - Sans pilote section should show no windows
        return []
    }

    // Non-pilot scenario (no shoal configured) - use dock windows
    if (dockWindows && dockWindows.length > 0) {
        return dockWindows
    }

    return []
}

export function mapDockWindowsToShoalWindowsMinMax(dockWindows, shoalWindows) {
    if (!dockWindows || dockWindows.length === 0) {
        return []
    }

    if (!shoalWindows || shoalWindows.length === 0) {
        return dockWindows.map(() => ({ start: null, end: null, windowTooShort: false }))
    }

    return dockWindows.map(dockWindow => {
        if (!dockWindow.start || !dockWindow.end) {
            return { start: null, end: null, windowTooShort: false }
        }

        const dockStart = new Date(dockWindow.start).getTime()
        const dockEnd = new Date(dockWindow.end).getTime()

        // Find the shoal window that overlaps with this dock window
        const matchingShoal = shoalWindows.find(sw => {
            if (!sw || !sw.min || !sw.max) return false

            const shoalStart = new Date(sw.min).getTime()
            const shoalEnd = new Date(sw.max).getTime()

            return shoalStart < dockEnd && shoalEnd > dockStart
        })

        if (!matchingShoal) {
            return { start: null, end: null, windowTooShort: false }
        }

        const duration = new Date(matchingShoal.max) - new Date(matchingShoal.min)

        return {
            start: matchingShoal.min,
            end: matchingShoal.max,
            windowTooShort: duration < 60 * 60 * 1000,
        }
    })
}
